import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '../../../../Components/uielements';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import { Form } from '@kuzmycz/react-form-ula';
import {
  ModalButtonWrapper,
  ModalGap,
  ModalWrapper,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import { accountTransfer } from '../../../../services/bioplus-services/auth';
import { Error } from '@mui/icons-material';
import { WarningCard } from '../../../../Components/uielements/warningCard';

// Environment-based country config
const COUNTRY_CONFIG = {
  au_stage: { code: '+61', digits: 9, country: 'Australia' },
  au_production: { code: '+61', digits: 9, country: 'Australia' },
  production: { code: '+1', digits: 10, country: 'USA' },
  default: { code: '+61', digits: 9, country: 'Australia' },
};

// Styled Components
const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 16px;
`;

const PhoneInputGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CountryCodeInput = styled.div`
  position: relative;
  width: 90px;
  flex-shrink: 0;
`;

const CountryCodeDisplay = styled.div`
  position: relative;
  width: 100%;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.2s ease;
  border-color: rgba(255, 255, 255, 0.3);

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const PhoneNumberInput = styled.div`
  flex: 1;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #f87171;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HelpText = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
`;

const SuccessMessage = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 6px;
  color: #86efac;
  font-size: 13px;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const getCountryConfig = (env) => {
  const config = COUNTRY_CONFIG[env] || COUNTRY_CONFIG.default;
  return config;
};

const AccountTransferModal = ({ onClose, environment = 'default' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const countryConfig = useMemo(() => getCountryConfig(environment), [environment]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === countryConfig.digits;
  };

  const handleTransferAccount = async (values) => {
    // Clear previous errors and success
    setError('');
    setSuccess(false);

    // Validate emails are provided
    if (!values.email || !values.confirmEmail) {
      setError('Both email fields are required.');
      return;
    }

    // Trim and normalize emails
    const email = values.email.trim().toLowerCase();
    const confirmEmail = values.confirmEmail.trim().toLowerCase();

    // Validate email format
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate emails match
    if (email !== confirmEmail) {
      setError('Email addresses do not match.');
      return;
    }

    // Validate phone number is provided
    if (!values.phone) {
      setError('Phone number is required.');
      return;
    }

    // Validate phone number format
    if (!validatePhoneNumber(values.phone)) {
      setError(
        `Phone number must contain exactly ${countryConfig.digits} digits for ${countryConfig.country}.`
      );
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Extract digits only and combine with country code
      const phoneDigitsOnly = values.phone.replace(/\D/g, '');
      const transfereeContactNumber = `${countryConfig.code}${phoneDigitsOnly}`;

      const payload = {
        transfereeEmail: email,
        transfereeContactNumber: transfereeContactNumber,
      };

      // Call the account transfer service
      await accountTransfer(payload);

      setSuccess(true);
      setTimeout(() => {
        navigate('/signout');
        onClose();
      }, 2000);
    } catch (err) {
      setError('Member Account Already Exists!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Transfer Account">
      <Form
        initialValues={{ email: '', confirmEmail: '', phone: '' }}
        onSubmit={handleTransferAccount}
      >
        <ModalWrapper style={{ maxWidth: '600px' }}>
          <ModalGap>
            {success && (
              <SuccessMessage>
                ✓ Account transferred successfully. You will be logged out shortly.
              </SuccessMessage>
            )}

            {/* Email Field */}
            <InputWrapper>
              <BioPlusTextField
                id="email"
                name="email"
                type="email"
                label="Enter email address"
                fullWidth
                autoComplete="off"
              />
            </InputWrapper>

            {/* Confirm Email Field */}
            <InputWrapper>
              <BioPlusTextField
                id="confirmEmail"
                name="confirmEmail"
                type="email"
                label="Re-enter email address"
                fullWidth
                autoComplete="off"
              />
            </InputWrapper>

            {/* Phone Number Field with Country Code */}
            <InputWrapper>
              <PhoneInputGroup>
                <CountryCodeInput>
                  <CountryCodeDisplay>{countryConfig.code}</CountryCodeDisplay>
                </CountryCodeInput>
                <PhoneNumberInput>
                  <BioPlusTextField
                    name="phone"
                    type="tel"
                    label={'Phone number'}
                    fullWidth
                    autoComplete="off"
                    maxLength={countryConfig.code === '+61' ? 9 : 10}
                  />
                </PhoneNumberInput>
              </PhoneInputGroup>
              <HelpText>
                {countryConfig.digits} digits required for {countryConfig.country}
              </HelpText>
            </InputWrapper>

            {/* Error Message */}
            {error && <ErrorText>{error}</ErrorText>}
          </ModalGap>

          <WarningCard
            title="Verify email carefully"
            message="Please verify the Transferee Email carefully before proceeding with the account transfer. Once the transfer is completed, this action cannot be undone."
            icon={<Error />}
          />

          {/* Action Buttons */}
          <ModalButtonWrapper>
            <OutlineButton type="button" onClick={onClose} disabled={loading || success}>
              Close
            </OutlineButton>
            <Button varient="normal" type="submit" disabled={loading || success}>
              {loading ? 'Transferring…' : 'Transfer Account'}
            </Button>
          </ModalButtonWrapper>
        </ModalWrapper>
      </Form>
    </Modal>
  );
};

export default AccountTransferModal;
